/**
Copyright © 2024 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/
import codes from 'country-calling-code';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import countries from 'i18n-iso-countries';
import camelCase from 'lodash-es/camelCase';
import find from 'lodash-es/find';
import isNil from 'lodash-es/isNil';
import pickBy from 'lodash-es/pickBy';
import range from 'lodash-es/range';
import { number } from 'yup';
import { ACTIVITY_HISTORY_TYPE } from '@hems/util/src/constant/activityHistory';
import { alpha2CountryList, auAlpha2CountryList, euAlpha2CountryList, usAlpha2CountryList, NUMBER_UNIT, SORT_ORDER, APP_MODE, DEVICE_CONNECTION_STATUS, IMAGE_FILE_EXTENSION, } from '@hems/util/src/constant/constant';
import { AC_MODEL_TYPE } from '@hems/util/src/constant/device';
import { DEFAULT_LANGUAGE, INTL_LOCALE_MAP, LANGUAGE } from '@hems/util/src/constant/languages';
import { localStorageKey } from '@hems/util/src/constant/localStorageKey';
import { UNIT } from '@hems/util/src/constant/unit';
import { getEndDate, getSixDaysAgo, now } from '@hems/util/src/helper/dateHelper';
import { formatUnitNumber } from '@hems/util/src/helper/numberformatHelper';
import { isLanguageValueType, isLocaleValueType } from '@hems/util/src/helper/tsguardHelper';
dayjs.extend(utc);
/* eslint-disable */
export const getUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0, v = c == 'x' ? r : (r & 3) | 8;
        return v.toString(16);
    });
};
export const isNull = (str) => {
    return (typeof str === 'string' && str.trim() === '') || str === undefined || str === null;
};
export const padLeft = (number, length, padString = '0') => {
    const sign = number < 0 ? '-' : '';
    const padStrLength = length - Math.abs(number).toString().length < 0 ? 0 : length - Math.abs(number).toString().length;
    const padStr = Array(padStrLength + 1).join(padString);
    return sign + padStr + number;
};
export const formatNumber = (_num, decimal = 0) => {
    if (_num === undefined || _num === null)
        return null;
    const num = typeof _num === 'string' ? Number(_num) : _num;
    if (isNaN(num))
        return null;
    const number = num.toFixed(decimal);
    return number.replace(/^[+-]?\d+/, (int) => {
        return int.replace(/(\d)(?=(\d{3})+$)/g, '$1,');
    });
};
export const getNumberFormatter = (langCd, options = {}) => {
    return new Intl.NumberFormat(isLocaleValueType(langCd) ? INTL_LOCALE_MAP[langCd] : DEFAULT_LANGUAGE, options);
};
export const nullTo = (value, defaultValue) => {
    return isNil(value) ? defaultValue : value;
};
export const keyToCamelCase = (obj) => {
    const retObj = {};
    for (let key in obj) {
        const newKey = camelCase(key);
        retObj[newKey] = obj[key];
    }
    return retObj;
};
export const mapCodeNameToSelectorOption = (codeName, t) => {
    return {
        text: t(codeName.name),
        value: codeName.code,
        ...(codeName.description && { description: codeName.description }),
    };
};
export const codeNamesToSelectorOptions = (codeNames, t, first) => {
    const firstItem = [];
    if (typeof first === 'boolean' && first === true) {
        firstItem.push({ text: '', value: null });
    }
    else if (typeof first === 'object') {
        firstItem.push(first);
    }
    return firstItem.concat(codeNames.map((item) => mapCodeNameToSelectorOption(item, t)));
};
export const downloadCSV = (data, columns, fileName = 'export') => {
    const _fileName = `${fileName}_${dayjs().format('YYYYMMDDHHmmss')}`;
    const header = columns.map((item) => `"${item.header}"`).join(',');
    const headerId = columns.map((item) => item.name);
    const rows = [];
    data.forEach((item) => {
        const row = [];
        for (const id of headerId) {
            if (['device_id', 'grp_nm', 'mpn_no', 'user_id', 'comn_nm', 'site_id', 'battery1', 'battery2', 'battery3'].includes(id)) {
                row.push(`"${isNull(item[id]) ? '' : item[id]}\t"`);
            }
            else {
                row.push(`"${isNull(item[id]) ? '' : item[id]}"`);
            }
        }
        rows.push(row.join(','));
    });
    const csvData = header + '\r\n' + rows.join('\r\n');
    const exportedFilenmae = _fileName + '.csv';
    const blob = new Blob(['\ufeff' + csvData], { type: 'text/csv;charset=utf-8;' });
    if (navigator.msSaveBlob) {
        // IE 10+
        navigator.msSaveBlob(blob, exportedFilenmae);
    }
    else {
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', exportedFilenmae);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }
};
export const getOperStatusName = (operStusStr) => {
    switch (operStusStr) {
        case 'code.operstuscd.0':
            return 'device.normal';
        case 'code.operstuscd.1':
            return 'device.warning';
        case 'code.operstuscd.2':
            return 'common.error';
        case 'code.operstuscd.4':
            return 'common.incomplete';
        default:
            return '';
    }
};
export const capitalize = (str) => {
    return str ? str.charAt(0).toUpperCase() + str.toLocaleLowerCase().substring(1) : '';
};
export const isJSON = (jsonString) => {
    try {
        return JSON.parse(jsonString) && !!jsonString;
    }
    catch (e) {
        return false;
    }
};
export const getAppEnv = () => process.env.VUE_APP_ENV;
export const getEnvLocale = () => process.env.VUE_APP_LOCALE;
export const getDialCodeByCountry = (locale) => {
    const languageList = countries.getSupportedLanguages();
    languageList.forEach((item) => {
        countries.registerLocale(require(`i18n-iso-countries/langs/${item}.json`));
    });
    let countryAndLang = alpha2CountryList;
    if (locale) {
        switch (locale) {
            case 'EU':
                countryAndLang = euAlpha2CountryList;
                break;
            case 'AU':
            case 'NZ':
                countryAndLang = auAlpha2CountryList.concat(euAlpha2CountryList);
                break;
            case 'US':
                countryAndLang = usAlpha2CountryList.concat(euAlpha2CountryList);
                break;
            default:
                countryAndLang = alpha2CountryList;
        }
    }
    return countryAndLang.map((item) => ({
        name: countries.getName(item.country, item.lang),
        dial_code: `+${find(codes, { isoCode2: item.country })?.countryCodes[0]}`,
    }));
};
export const getTemperatureUnit = (type = 'C') => {
    return type === 'F' ? '°F' : '°C';
};
export const preventKeyEnter = (event) => {
    if (event.type === 'keydown') {
        if (!(event.target.type === 'textarea')) {
            event.preventDefault();
            return false;
        }
    }
    return true;
};
export const getCategoryNameByCode = (category, t) => {
    const codeNames = [
        {
            code: category.slice(-1),
            name: category,
        },
    ];
    return codeNamesToSelectorOptions(codeNames, t, false)[0].text;
};
export const getLangCd = () => {
    const lang = localStorage.getItem(localStorageKey.language);
    return lang && isLanguageValueType(lang) ? lang : LANGUAGE.EN;
};
export const convertDecimalToHex = (value, digit) => {
    return value.toString(16).toUpperCase().padStart(digit, '0');
};
export const getACModelType = (productModelNm) => {
    if (!productModelNm)
        return AC_MODEL_TYPE.AC_ESS;
    if (productModelNm.includes(AC_MODEL_TYPE.AC_ESS))
        return AC_MODEL_TYPE.AC_ESS;
    if (productModelNm.includes(AC_MODEL_TYPE.AC_COMBINER))
        return AC_MODEL_TYPE.AC_COMBINER;
    return AC_MODEL_TYPE.AC_ESS;
};
export const isEmptyArray = (arr) => {
    return arr.length === 0;
};
export const attachUnit = (value, unit) => {
    if (value === undefined || value === null)
        return null;
    return `${value} [${unit}]`;
};
export const attachUnitToTitle = (title, unit) => {
    return `${title} [${unit}]`;
};
export const getKwhData = (arr) => {
    const sum = arr.reduce((prev, curr) => (prev ?? 0) + (curr ?? 0) * NUMBER_UNIT.THOUSAND, 0) ?? 0;
    const { origin, formattedNumber, unit } = formatUnitNumber(sum, UNIT.WATT_HOUR);
    return { origin, formattedNumber, unit };
};
export const getConnectStatus = (isConnect, operStatusCd) => {
    if (isConnect) {
        return DEVICE_CONNECTION_STATUS['ONLINE'];
    }
    else if (operStatusCd in DEVICE_CONNECTION_STATUS) {
        return DEVICE_CONNECTION_STATUS[operStatusCd];
    }
    else {
        return DEVICE_CONNECTION_STATUS['ONLINE_DISCONNECTED'];
    }
};
export const addSortOrderMark = (sortColumnName, sortOrder) => {
    return sortOrder === SORT_ORDER.ASCENDING ? sortColumnName : `-${sortColumnName}`;
};
export const addSortOrderMarkNew = (sortColumnName, sortOrder) => {
    return `${sortOrder === SORT_ORDER.ASCENDING ? '+' : '-'}${sortColumnName}`;
};
export const getSortNameAndOrder = (sort) => {
    const sortColumnName = sort.replace(/^-/, '');
    const isAscending = !sort.startsWith('-');
    return { sortColumnName, isAscending };
};
export const getOrderStatus = (sort) => {
    return !sort.startsWith('-');
};
export const getSelectorValueByOptions = (options, targetValue) => options.find((option) => option.value === targetValue)?.text;
export const isEmptyString = (value) => {
    return value === '';
};
const createFileUrl = (file, fileName, fileExtension, dateFormat) => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(file);
    link.download = `${fileName}_${dayjs().utc().format(dateFormat)}.${fileExtension}`;
    return link;
};
export const downloadFile = (file, fileName, fileExtension, dateFormat = 'YYYYMMDDHHmmss') => {
    const link = createFileUrl(file, fileName, fileExtension, dateFormat);
    link.click();
    link.remove();
};
export const defaultInputErrorInfo = {
    isError: false,
    errorMessage: '',
};
export const getNumberLeftPadStringArrayInRange = (startNumber, endNumber, step = 1, padLength = 2) => range(startNumber, endNumber + 1, step).map((numberValue) => padLeft(numberValue, padLength));
/** 범위 겹칩 체크 */
export const isOverlapping = (start1, end1, start2, end2) => start1 < end2 && start2 < end1;
export const getIntlLocale = () => INTL_LOCALE_MAP[getLangCd()];
export const downloadButtonIconInfo = {
    src: 'fleet/ic_download',
    extension: IMAGE_FILE_EXTENSION.SVG,
};
export const activityHistoryInitialConditions = {
    date: {
        start: getSixDaysAgo(),
        end: getEndDate(),
    },
    activities: Object.values(ACTIVITY_HISTORY_TYPE),
    search: '',
};
export const initialNoticeItem = {
    noticeId: 0,
    title: '',
    contents: [],
    category: '',
    lang: '',
    isActive: false,
    noticeTarget: '',
    homeAppVer: '',
    proAppVer: '',
    date: {
        start: now(),
        end: now(),
    },
    startDt: '',
    endDt: '',
};
export const isLocalEnv = process.env.VUE_APP_ENV === APP_MODE.LOCAL;
export const filterNilOrEmptyValues = (obj) => pickBy(obj, (item) => !isNil(item) && (typeof item !== 'string' || !isEmptyString(item)));
export const getNumberValidator = () => number()
    .transform((v, o) => (o === '' ? null : v))
    .nullable();
